import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Layout from './components/Layout/Layout';
import PageTitle from './components/Common/PageTitle';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import VerifyEmail from './pages/Auth/VerifyEmail';
import VerifyEmailNotice from './pages/Auth/VerifyEmailNotice';
import SubscriptionExpired from './pages/Auth/SubscriptionExpired';
import PublicPortfolioView from './pages/Public/PublicPortfolioView';
import SetupWizard from './pages/Setup/SetupWizard';
import SetupPending from './pages/Setup/SetupPending';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile/Profile';
import OfficeSettings from './pages/Settings/OfficeSettings';
import SubscriptionInfo from './pages/Settings/SubscriptionInfo';
import ProjectList from './pages/Projects/ProjectList';
import ProjectForm from './pages/Projects/ProjectForm';
import ProjectDetail from './pages/Projects/ProjectDetail';
import ProjectBlockDetail from './pages/Projects/ProjectBlockDetail';
import PortfolioForm from './pages/Portfolios/PortfolioForm';
import PortfolioDetail from './pages/Portfolios/PortfolioDetail';
import PortfolioFilteredList from './pages/Portfolios/PortfolioFilteredList';
import ShareRequests from './pages/Portfolios/ShareRequests';
import CustomerList from './pages/Customers/CustomerList';
import CustomerForm from './pages/Customers/CustomerForm';
import CustomerDetail from './pages/Customers/CustomerDetail';
import PotentialCustomers from './pages/Customers/PotentialCustomers';
import ActiveCustomers from './pages/Customers/ActiveCustomers';
import MeetingList from './pages/Meetings/MeetingList';
import MeetingForm from './pages/Meetings/MeetingForm';
import MeetingDetail from './pages/Meetings/MeetingDetail';
import MeetingComplete from './pages/Meetings/MeetingComplete';
import TodayMeetings from './pages/Meetings/TodayMeetings';
import FutureMeetings from './pages/Meetings/FutureMeetings';
import PastMeetings from './pages/Meetings/PastMeetings';
import SaleList from './pages/Sales/SaleList';
import RentalList from './pages/Rentals/RentalList';
import DownPaymentList from './pages/DownPayments/DownPaymentList';
import PersonnelCommissions from './pages/Commissions/PersonnelCommissions';
import OfficeFinance from './pages/Finance/OfficeFinance';
import DepartmentList from './pages/Departments/DepartmentList';
import DepartmentForm from './pages/Departments/DepartmentForm';
import DepartmentDetail from './pages/Departments/DepartmentDetail';
import UserList from './pages/Departments/UserList';
import UserForm from './pages/Departments/UserForm';
import UserDetail from './pages/Departments/UserDetail';
import ActivityList from './pages/Departments/ActivityList';
import DepartmentPermissions from './pages/Departments/DepartmentPermissions';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: '12px',
              padding: '16px 20px',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(10px)',
            },
            success: {
              duration: 3000,
              style: {
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#10b981',
              },
            },
            error: {
              duration: 4000,
              style: {
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#ef4444',
              },
            },
            loading: {
              style: {
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#fff',
              },
            },
          }}
        />
        <Router>
          <Routes>
            <Route path="/login" element={
              <PageTitle title="Giriş Yap">
                <Login />
              </PageTitle>
            } />
            <Route path="/register" element={
              <PageTitle title="Kayıt Ol">
                <Register />
              </PageTitle>
            } />
            <Route path="/subscription-expired" element={
              <PageTitle title="Abonelik">
                <SubscriptionExpired />
              </PageTitle>
            } />
            <Route path="/forgot-password" element={
              <PageTitle title="Şifremi Unuttum">
                <ForgotPassword />
              </PageTitle>
            } />
            <Route path="/reset-password" element={
              <PageTitle title="Şifre Sıfırla">
                <ResetPassword />
              </PageTitle>
            } />
            <Route path="/verify-email/:id/:hash" element={
              <PageTitle title="E-posta Doğrula">
                <VerifyEmail />
              </PageTitle>
            } />
            <Route path="/verify-email-notice" element={
              <PageTitle title="E-posta Doğrulaması Gerekli">
                <VerifyEmailNotice />
              </PageTitle>
            } />
            <Route path="/p/:token" element={
              <PageTitle title="Portföy İnceleme">
                <PublicPortfolioView />
              </PageTitle>
            } />
            <Route path="/setup" element={
              <ProtectedRoute>
                <PageTitle title="Kurulum Sihirbazı">
                  <SetupWizard />
                </PageTitle>
              </ProtectedRoute>
            } />
            <Route path="/setup-pending" element={
              <ProtectedRoute>
                <PageTitle title="Kurulum Bekleniyor">
                  <SetupPending />
                </PageTitle>
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <PageTitle title="Dashboard">
                    <Dashboard />
                  </PageTitle>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/*" element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    {/* Profil */}
                    <Route path="/profile" element={<PageTitle title="Profilim"><Profile /></PageTitle>} />

                    {/* Projeler */}
                    <Route path="/projects" element={<PageTitle title="Projeler"><ProjectList /></PageTitle>} />
                    <Route path="/projects/new" element={<PageTitle title="Yeni Proje"><ProjectForm /></PageTitle>} />
                    <Route path="/projects/:id/edit" element={<PageTitle title="Proje Düzenle"><ProjectForm /></PageTitle>} />
                    <Route path="/projects/:id" element={<PageTitle title="Proje Detay"><ProjectDetail /></PageTitle>} />
                    <Route path="/project-blocks/:id" element={<PageTitle title="Blok Detay"><ProjectBlockDetail /></PageTitle>} />

                    {/* Portföyler */}
                    <Route path="/portfolios" element={<PageTitle title="Tüm Portföyler"><PortfolioFilteredList key="all" title="Tüm Portföyler" /></PageTitle>} />
                    <Route path="/portfolios/my" element={<PageTitle title="Portföylerim"><PortfolioFilteredList key="all" myPortfolio="true" title="Portföylerim" /></PageTitle>} />
                    <Route path="/portfolios/sales" element={<PageTitle title="Satılık İlanlar"><PortfolioFilteredList key="sale" title="Satılık İlanlar" listingType="sale" /></PageTitle>} />
                    <Route path="/portfolios/rentals" element={<PageTitle title="Kiralık İlanlar"><PortfolioFilteredList key="rental" title="Kiralık İlanlar" listingType="rental" /></PageTitle>} />
                    <Route path="/portfolios/share-requests" element={<PageTitle title="Paylaşım Talepleri"><ShareRequests /></PageTitle>} />
                    <Route path="/portfolios/new" element={<PageTitle title="Yeni Portföy"><PortfolioForm /></PageTitle>} />
                    <Route path="/portfolios/:id/edit" element={<PageTitle title="Portföy Düzenle"><PortfolioForm /></PageTitle>} />
                    <Route path="/portfolios/:id" element={<PageTitle title="Portföy Detay"><PortfolioDetail /></PageTitle>} />

                    {/* Müşteriler */}
                    <Route path="/customers" element={<PageTitle title="Müşteriler"><CustomerList /></PageTitle>} />
                    <Route path="/customers/potential" element={<PageTitle title="Potansiyel Müşteriler"><PotentialCustomers /></PageTitle>} />
                    <Route path="/customers/active" element={<PageTitle title="Aktif Müşteriler"><ActiveCustomers /></PageTitle>} />
                    <Route path="/customers/new" element={<PageTitle title="Yeni Müşteri"><CustomerForm /></PageTitle>} />
                    <Route path="/customers/:id/edit" element={<PageTitle title="Müşteri Düzenle"><CustomerForm /></PageTitle>} />
                    <Route path="/customers/:id" element={<PageTitle title="Müşteri Detay"><CustomerDetail /></PageTitle>} />

                    {/* Görüşmeler */}
                    <Route path="/meetings" element={<PageTitle title="Görüşmeler"><MeetingList /></PageTitle>} />
                    <Route path="/meetings/today" element={<PageTitle title="Bugünkü Görüşmeler"><TodayMeetings /></PageTitle>} />
                    <Route path="/meetings/future" element={<PageTitle title="Gelecek Görüşmeler"><FutureMeetings /></PageTitle>} />
                    <Route path="/meetings/past" element={<PageTitle title="Geçmiş Görüşmeler"><PastMeetings /></PageTitle>} />
                    <Route path="/meetings/new" element={<PageTitle title="Yeni Görüşme"><MeetingForm /></PageTitle>} />
                    <Route path="/meetings/:id/edit" element={<PageTitle title="Görüşme Düzenle"><MeetingForm /></PageTitle>} />
                    <Route path="/meetings/:id/complete" element={<PageTitle title="Görüşme Tamamla"><MeetingComplete /></PageTitle>} />
                    <Route path="/meetings/:id" element={<PageTitle title="Görüşme Detay"><MeetingDetail /></PageTitle>} />

                    {/* Satışlar & Kapora */}
                    <Route path="/sales" element={<PageTitle title="Satışlar"><SaleList /></PageTitle>} />
                    <Route path="/rentals" element={<PageTitle title="Kiralamalar"><RentalList /></PageTitle>} />
                    <Route path="/down-payments" element={<PageTitle title="Kapora Ödemeleri"><DownPaymentList /></PageTitle>} />
                    <Route path="/my-commissions" element={<PageTitle title="Komisyonlarım"><PersonnelCommissions /></PageTitle>} />
                    <Route path="/finance/office" element={<PageTitle title="Ofis Finans"><OfficeFinance /></PageTitle>} />

                    {/* Departmanlar & Kullanıcılar */}
                    <Route path="/departments" element={<PageTitle title="Departmanlar"><DepartmentList /></PageTitle>} />
                    <Route path="/departments/new" element={<PageTitle title="Yeni Departman"><DepartmentForm /></PageTitle>} />
                    <Route path="/departments/users" element={<PageTitle title="Kullanıcılar"><UserList /></PageTitle>} />
                    <Route path="/departments/users/new" element={<PageTitle title="Yeni Kullanıcı"><UserForm /></PageTitle>} />
                    <Route path="/departments/users/:id/edit" element={<PageTitle title="Kullanıcı Düzenle"><UserForm /></PageTitle>} />
                    <Route path="/departments/users/:id" element={<PageTitle title="Personel Detayı"><UserDetail /></PageTitle>} />
                    <Route path="/departments/activities" element={<PageTitle title="Aktiviteler"><ActivityList /></PageTitle>} />
                    <Route path="/departments/:id/edit" element={<PageTitle title="Departman Düzenle"><DepartmentForm /></PageTitle>} />
                    <Route path="/departments/:id" element={<PageTitle title="Departman Detay"><DepartmentDetail /></PageTitle>} />
                    <Route path="/departments/permissions" element={<PageTitle title="Departman Yetkileri"><DepartmentPermissions /></PageTitle>} />

                    {/* Ayarlar */}
                    <Route path="/settings/office" element={<PageTitle title="Ofis Ayarları"><OfficeSettings /></PageTitle>} />
                    <Route path="/settings/subscription" element={<PageTitle title="Abonelik"><SubscriptionInfo /></PageTitle>} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
